import { api, handleApiError, requestInterceptor, requestErrorInterceptor, responseInterceptor, responseErrorInterceptor } from './api';
import type {
    ConversationTranscript,
    KeyMoment
    // ConversationSummaryData and ConversationSummary are excluded as requested
} from '../types/analytics';
import { transformSummaryDocument } from '../utils/summaryDocumentTransformer';
import axios from 'axios'; // Import axios

// Type for Gemini API responses
interface GeminiSummaryResponse {
    content: string;
}

// Create a new Axios instance specifically for conversation service
const CONVERSATION_API_URL = 'https://staging.conversation.convrsespaces.com';

const conversationApi = axios.create({
    baseURL: CONVERSATION_API_URL
});

// Apply the same interceptors from api.ts
conversationApi.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
conversationApi.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

class ConversationService {

    // Get conversation transcript
    async getTranscript(conversationId: string, page: number = 1, pageSize: number = 50): Promise<ConversationTranscript> {
        try {
            const response = await conversationApi.get<ConversationTranscript>(`/conversations/${conversationId}/transcript`, {
                // Interceptor handles auth header
                params: { page, page_size: pageSize }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    // Get conversation key moments
    async getKeyMoments(conversationId: string, momentType?: string): Promise<KeyMoment[]> {
        try {
            const response = await conversationApi.get<KeyMoment[]>(`/conversations/${conversationId}/key-moments`, {
                // Interceptor handles auth header
                params: { moment_type: momentType }
            });
            // Assuming the API returns KeyMoment[] directly
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    // Search conversations
    async searchConversations(query: string, page: number = 1, size: number = 10): Promise<any> { // Replace 'any' with specific response type if known
        try {
            const response = await conversationApi.get('/search/semantic', {
                // Interceptor handles auth header
                params: {
                    query,
                    page,
                    size,
                    context_window: 3 // Keep or adjust as needed
                }
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    // Advanced search
    async advancedSearch(params: {
        start_date?: string;
        end_date?: string;
        speakers?: string[];
        topics?: string[];
        duration_range?: { min: number; max: number };
        sentiment_range?: { min: number; max: number }; // Keep or remove based on dashboard needs
        participant_count?: { min: number; max: number };
        languages?: string[];
        action_items?: boolean; // Keep or remove based on dashboard needs
        page?: number;
        size?: number;
        sort_by?: string;
        sort_order?: 'asc' | 'desc';
    }): Promise<any> { // Replace 'any' with specific response type if known
        try {
            const response = await conversationApi.post('/search/advanced', params, {
                // Interceptor handles auth header
            });
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    // Excluded methods (deprecated or removed):
    // getSummary(conversationId: string): Promise<ConversationSummary>
    // getSentiment(conversationId: string, granularity: 'speaker' | 'overall' = 'speaker')
    // getActionItems(conversationId: string)
    // getTopics(conversationId: string)

    // --- New Generic Endpoints ---

    /**
     * Get full analysis summary document
     * GET /aggregations/conversation/{conversation_id}/summary_document
     */
    /**
     * Fetch a conversation's audio recording URL from the recording Lambda.
     * Returns a ready-made CloudFront URL (the frontend never builds it).
     * POST { businessId, conversationId } -> { recordingUrl } | { error }
     */
    async getConversationRecording(businessId: string, conversationId: string): Promise<string> {
        const response = await fetch(
            "https://9pqye6b40h.execute-api.us-east-1.amazonaws.com/default/getConversationRecording",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ businessId, conversationId }),
            }
        );
        const data = await response.json();
        if (data.error) throw new Error(data.error);                 // e.g. {"error":"No recording found"}
        if (!response.ok) throw new Error(`Failed to fetch recording URL: ${response.statusText}`);
        if (!data.recordingUrl) throw new Error('Recording URL not found in response');
        return data.recordingUrl;                                    // CloudFront URL
    }

    async getSummaryDocument(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/aggregations/conversation/${conversationId}/summary_document`);
            // The endpoint now returns structured section objects instead of
            // ready-made markdown strings. Adapt it back to the legacy shape the
            // MeetingDetailsPage rendering pipeline expects (old responses pass through).
            return transformSummaryDocument(response.data) as GeminiSummaryResponse;
        } catch (error) {
            return handleApiError(error);
        }
    }



    // --- Legacy Gemini Summary Endpoints (Deprecated) ---

    async getProcessingStatus(requestId: string): Promise<{ data: { status: string } }> {
        try {
            const response = await conversationApi.get<{ data: { status: string } }>(`/status/${requestId}`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /** @deprecated Use getSummaryDocument instead */
    async getGeminiClientSnapshot(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/conversations/${conversationId}/gemini/client-snapshot`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /** @deprecated Use getSummaryDocument instead */
    async getGeminiStrategicSummary(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/conversations/${conversationId}/gemini/strategic-summary`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /** @deprecated Use getSummaryDocument instead */
    async getGeminiDriversRisk(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/conversations/${conversationId}/gemini/drivers-risk`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /** @deprecated Use getSummaryDocument instead */
    async getGeminiProductOptions(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/conversations/${conversationId}/gemini/product-options`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /** @deprecated Use getSummaryDocument instead */
    async getGeminiEmotionalArc(conversationId: string): Promise<GeminiSummaryResponse> {
        try {
            const response = await conversationApi.get<GeminiSummaryResponse>(`/conversations/${conversationId}/gemini/emotional-arc`);
            return response.data;
        } catch (error) {
            return handleApiError(error);
        }
    }
}

export const conversationService = new ConversationService(); 